import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useChatApi, useChatByQuoteId } from 'src/api/db/apiChat'
import { useMessageApi, type Message_ } from 'src/api/db/apiMessage'
import { queryKeys } from 'src/api/db/queryKeys'
import { useMerchGPT } from 'src/api/server/useMerchGPT'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { trackEvent } from 'src/api/db/apiAnalytics'
import { useSupabaseClient } from 'src/api/supabase'
import {
  computeSaraLoading,
  lastSaraMessageAwaitsAssistant,
} from './saraChatLoading'

export const useRequestSaraChat = ({
  quoteId,
  orgId,
}: {
  quoteId: number | undefined
  orgId: string | undefined
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { chat } = useChatByQuoteId(quoteId)
  const { createChat } = useChatApi({ org_id: orgId })
  const message = useMessageApi()
  const merchGPT = useMerchGPT()
  const supabase = useSupabaseClient()

  const saraMessages = chat?.message ?? []
  /** Set when this client kicks off a Sara turn; cleared when a reply appears or we give up. */
  const [expectingReply, setExpectingReply] = useState(false)
  const [giveUpWaiting, setGiveUpWaiting] = useState(false)

  const lastAwaitsAssistant = useMemo(
    () => lastSaraMessageAwaitsAssistant(saraMessages),
    [saraMessages]
  )

  const isWaitingForSara =
    expectingReply && lastAwaitsAssistant && !giveUpWaiting
  const isSaraLoading = computeSaraLoading({
    isMerchGptPending: merchGPT.isPending,
    expectingReply,
    lastAwaitsAssistant,
    giveUpWaiting,
  })
  const wasSaraLoadingRef = useRef(false)

  useEffect(() => {
    if (expectingReply && !lastAwaitsAssistant) {
      setExpectingReply(false)
      setGiveUpWaiting(false)
    }
  }, [expectingReply, lastAwaitsAssistant])

  useEffect(() => {
    if (wasSaraLoadingRef.current && !isSaraLoading && quoteId) {
      void queryClient.refetchQueries({
        queryKey: [queryKeys.quoteItems, quoteId],
      })
    }
    wasSaraLoadingRef.current = isSaraLoading
  }, [isSaraLoading, quoteId, queryClient])

  useEffect(() => {
    if (!isSaraLoading || !quoteId) return

    const pollInterval = setInterval(() => {
      void queryClient.refetchQueries({
        queryKey: [queryKeys.chats, 'byQuote', quoteId],
      })
    }, 500)

    return () => clearInterval(pollInterval)
  }, [isSaraLoading, quoteId, queryClient])

  const stopWaitingAfterPolls = useCallback(
    (maxPolls: number) => {
      if (!quoteId) return () => undefined

      let polls = 0
      const pollInterval = setInterval(() => {
        polls += 1
        void queryClient.refetchQueries({
          queryKey: [queryKeys.chats, 'byQuote', quoteId],
        })
        if (polls >= maxPolls) {
          setGiveUpWaiting(true)
          setExpectingReply(false)
          clearInterval(pollInterval)
        }
      }, 500)

      return () => clearInterval(pollInterval)
    },
    [queryClient, quoteId]
  )

  // HTTP/timeout errors leave the last row as the user message. Poll briefly
  // in case the server still wrote a fallback, then stop the thinking state.
  useEffect(() => {
    if (!merchGPT.isError || !expectingReply) return
    return stopWaitingAfterPolls(4)
  }, [expectingReply, merchGPT.isError, stopWaitingAfterPolls])

  // Success with no assistant row (or slow cache) must not leave the spinner up.
  useEffect(() => {
    if (!merchGPT.isSuccess || !expectingReply || !lastAwaitsAssistant) return
    return stopWaitingAfterPolls(6)
  }, [
    expectingReply,
    lastAwaitsAssistant,
    merchGPT.isSuccess,
    stopWaitingAfterPolls,
  ])

  const ensureAgentChat = useCallback(async () => {
    if (chat?.id) return chat.id

    if (!quoteId) {
      toast({
        variant: 'destructive',
        title: t('Error', 'Fel'),
        description: t(
          'Unable to start Sara chat.',
          'Kunde inte starta Sara-chatt.'
        ),
      })
      return null
    }

    const created = await createChat.mutateAsync({
      org_id: orgId ?? null,
      name: t('Request chat', 'Förfråganchatt'),
      quote_id: quoteId,
    })

    const chatId = created.data?.id
    if (!chatId) return null

    await queryClient.invalidateQueries({
      queryKey: [queryKeys.chats, 'byQuote', quoteId],
    })

    return chatId
  }, [chat?.id, createChat, orgId, queryClient, quoteId, t, toast])

  const sendToSara = useCallback(
    async (
      cleanedText: string,
      files?: Array<{ url: string; name?: string; type?: string }>,
      options?: { isPrivate?: boolean; isAdmin?: boolean }
    ) => {
      const trimmed = cleanedText.trim()
      if (!trimmed && !files?.length) return

      const chatId = await ensureAgentChat()
      if (!chatId) return

      const created = await message.createMessage.mutateAsync({
        chat_id: chatId,
        content: JSON.stringify({
          message: trimmed,
          ...(options?.isPrivate ? { private: true } : {}),
          ...(options?.isAdmin ? { admin: true } : {}),
          ...(files?.length ? { files } : {}),
        }),
        role: 'user',
      })

      if (created.error || !created.data) {
        throw new Error(
          created.error?.message ??
            t('Failed to send message', 'Kunde inte skicka meddelandet')
        )
      }

      const createdMessage = created.data
      if (quoteId) {
        queryClient.setQueriesData(
          { queryKey: [queryKeys.chats, 'byQuote', quoteId] },
          (old: { message?: Message_[] } | null | undefined) => {
            if (!old) return old
            const msgs = old.message ?? []
            if (msgs.some((m) => m.id === createdMessage.id)) return old
            return { ...old, message: [...msgs, createdMessage] }
          }
        )
      }

      if (quoteId) {
        trackEvent(supabase, {
          name: 'chat_sent',
          quoteId,
        })
      }

      setGiveUpWaiting(false)
      setExpectingReply(true)
      merchGPT.mutate({
        chat_id: chatId,
        org_id: orgId,
      })
    },
    [
      ensureAgentChat,
      merchGPT,
      message.createMessage,
      orgId,
      queryClient,
      quoteId,
      supabase,
      t,
    ]
  )

  const stopSara = useCallback(() => {
    merchGPT.stop()
    setGiveUpWaiting(true)
    setExpectingReply(false)
  }, [merchGPT])

  return {
    chatId: chat?.id,
    saraMessages,
    isSaraLoading,
    isWaitingForSara,
    sendToSara,
    stopSara,
  }
}
